import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Sidebar from '@/components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { FileText, Plus, Download, PenTool, CheckCircle, Clock, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOLManagement = () => {
  const { user } = useAuth();
  const [bols, setBols] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBOL, setSelectedBOL] = useState(null);
  const [newBOL, setNewBOL] = useState({
    shipment_id: '',
    shipper_name: '',
    shipper_address: '',
    consignee_name: '',
    consignee_address: '',
    carrier_name: '',
    commodity_description: '',
    quantity: '',
    weight: '',
    freight_charges: '',
    special_instructions: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [bolsRes, shipmentsRes] = await Promise.all([
        axios.get(`${API}/bol`),
        axios.get(`${API}/shipments`)
      ]);
      setBols(bolsRes.data);
      setShipments(shipmentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBOL = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bol`, {
        ...newBOL,
        quantity: parseInt(newBOL.quantity),
        weight: parseFloat(newBOL.weight),
        freight_charges: parseFloat(newBOL.freight_charges)
      });
      toast.success('Bill of Lading created successfully');
      setCreateDialogOpen(false);
      setNewBOL({
        shipment_id: '',
        shipper_name: '',
        shipper_address: '',
        consignee_name: '',
        consignee_address: '',
        carrier_name: '',
        commodity_description: '',
        quantity: '',
        weight: '',
        freight_charges: '',
        special_instructions: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create BOL');
    }
  };

  const handleSignBOL = async (bolId, signatureType) => {
    try {
      await axios.post(`${API}/bol/${bolId}/sign`, { signature_type: signatureType });
      toast.success(`${signatureType.charAt(0).toUpperCase() + signatureType.slice(1)} signature added`);
      fetchData();
      if (selectedBOL) {
        const updatedBOL = await axios.get(`${API}/bol/${bolId}`);
        setSelectedBOL(updatedBOL.data);
      }
    } catch (error) {
      toast.error('Failed to sign BOL');
    }
  };

  const getSignatureStatus = (bol, type) => {
    const signed = bol[`signed_by_${type}`];
    const date = bol[`${type}_signature_date`];
    return { signed, date };
  };

  const shipmentsWithoutBOL = shipments.filter(
    s => !bols.find(b => b.shipment_id === s.id)
  );

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role={user?.role} />
      <main className="lg:ml-64 p-6 lg:p-10" data-testid="bol-management">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading font-bold tracking-tight">Bill of Lading</h1>
              <p className="text-muted-foreground mt-1">Manage shipping documents and e-signatures</p>
            </div>
            {(user?.role === 'distributor' || user?.role === 'dispatcher') && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90 shadow-glow" data-testid="create-bol-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create BOL
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Create Bill of Lading</DialogTitle>
                    <DialogDescription>Fill in the shipping document details</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateBOL} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Shipment</Label>
                      <Select
                        value={newBOL.shipment_id}
                        onValueChange={(value) => setNewBOL({ ...newBOL, shipment_id: value })}
                      >
                        <SelectTrigger data-testid="bol-shipment-select">
                          <SelectValue placeholder="Select a shipment" />
                        </SelectTrigger>
                        <SelectContent>
                          {shipmentsWithoutBOL.length === 0 ? (
                            <SelectItem value="none" disabled>No shipments available</SelectItem>
                          ) : (
                            shipmentsWithoutBOL.map((shipment) => (
                              <SelectItem key={shipment.id} value={shipment.id}>
                                {shipment.tracking_number} - {shipment.origin} to {shipment.destination}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="shipper_name">Shipper Name</Label>
                        <Input
                          id="shipper_name"
                          value={newBOL.shipper_name}
                          onChange={(e) => setNewBOL({ ...newBOL, shipper_name: e.target.value })}
                          required
                          data-testid="bol-shipper-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="consignee_name">Consignee Name</Label>
                        <Input
                          id="consignee_name"
                          value={newBOL.consignee_name}
                          onChange={(e) => setNewBOL({ ...newBOL, consignee_name: e.target.value })}
                          required
                          data-testid="bol-consignee-name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="shipper_address">Shipper Address</Label>
                        <Textarea
                          id="shipper_address"
                          value={newBOL.shipper_address}
                          onChange={(e) => setNewBOL({ ...newBOL, shipper_address: e.target.value })}
                          required
                          data-testid="bol-shipper-address"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="consignee_address">Consignee Address</Label>
                        <Textarea
                          id="consignee_address"
                          value={newBOL.consignee_address}
                          onChange={(e) => setNewBOL({ ...newBOL, consignee_address: e.target.value })}
                          required
                          data-testid="bol-consignee-address"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="carrier_name">Carrier Name</Label>
                      <Input
                        id="carrier_name"
                        value={newBOL.carrier_name}
                        onChange={(e) => setNewBOL({ ...newBOL, carrier_name: e.target.value })}
                        required
                        data-testid="bol-carrier-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="commodity_description">Commodity Description</Label>
                      <Textarea
                        id="commodity_description"
                        value={newBOL.commodity_description}
                        onChange={(e) => setNewBOL({ ...newBOL, commodity_description: e.target.value })}
                        required
                        data-testid="bol-commodity"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="quantity">Quantity</Label>
                        <Input
                          id="quantity"
                          type="number"
                          value={newBOL.quantity}
                          onChange={(e) => setNewBOL({ ...newBOL, quantity: e.target.value })}
                          required
                          data-testid="bol-quantity"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="weight">Weight (lbs)</Label>
                        <Input
                          id="weight"
                          type="number"
                          value={newBOL.weight}
                          onChange={(e) => setNewBOL({ ...newBOL, weight: e.target.value })}
                          required
                          data-testid="bol-weight"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="freight_charges">Freight Charges ($)</Label>
                        <Input
                          id="freight_charges"
                          type="number"
                          value={newBOL.freight_charges}
                          onChange={(e) => setNewBOL({ ...newBOL, freight_charges: e.target.value })}
                          required
                          data-testid="bol-freight"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="special_instructions">Special Instructions</Label>
                      <Textarea
                        id="special_instructions"
                        value={newBOL.special_instructions}
                        onChange={(e) => setNewBOL({ ...newBOL, special_instructions: e.target.value })}
                        data-testid="bol-instructions"
                      />
                    </div>

                    <Button type="submit" className="w-full bg-primary hover:bg-primary/90" data-testid="submit-bol-btn">
                      Create Bill of Lading
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total BOLs</p>
                    <p className="text-3xl font-heading font-bold">{bols.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-emerald-950/50">
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Fully Signed</p>
                    <p className="text-3xl font-heading font-bold">
                      {bols.filter(b => b.signed_by_shipper && b.signed_by_carrier && b.signed_by_consignee).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-amber-950/50">
                    <Clock className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Signatures</p>
                    <p className="text-3xl font-heading font-bold">
                      {bols.filter(b => !b.signed_by_shipper || !b.signed_by_carrier || !b.signed_by_consignee).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* BOL Table */}
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="font-heading">Documents</CardTitle>
              <CardDescription>All Bill of Lading documents</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>BOL Number</TableHead>
                      <TableHead>Shipper</TableHead>
                      <TableHead>Consignee</TableHead>
                      <TableHead>Carrier</TableHead>
                      <TableHead>Freight</TableHead>
                      <TableHead>Signatures</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bols.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No Bill of Lading documents yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      bols.map((bol) => (
                        <TableRow key={bol.id} data-testid={`bol-row-${bol.id}`}>
                          <TableCell className="font-mono text-sm">{bol.bol_number}</TableCell>
                          <TableCell>{bol.shipper_name}</TableCell>
                          <TableCell>{bol.consignee_name}</TableCell>
                          <TableCell>{bol.carrier_name}</TableCell>
                          <TableCell>${bol.freight_charges?.toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <span className={`w-2 h-2 rounded-full ${bol.signed_by_shipper ? 'bg-emerald-400' : 'bg-zinc-600'}`} title="Shipper" />
                              <span className={`w-2 h-2 rounded-full ${bol.signed_by_carrier ? 'bg-emerald-400' : 'bg-zinc-600'}`} title="Carrier" />
                              <span className={`w-2 h-2 rounded-full ${bol.signed_by_consignee ? 'bg-emerald-400' : 'bg-zinc-600'}`} title="Consignee" />
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedBOL(bol);
                                setViewDialogOpen(true);
                              }}
                              data-testid={`view-bol-${bol.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* View/Sign BOL Dialog */}
          <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              {selectedBOL && (
                <>
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Bill of Lading</DialogTitle>
                    <DialogDescription className="font-mono">{selectedBOL.bol_number}</DialogDescription>
                  </DialogHeader>
                  
                  {/* BOL Document Preview */}
                  <div className="bg-white text-black p-6 rounded-lg space-y-6">
                    <div className="text-center border-b border-gray-300 pb-4">
                      <h2 className="text-2xl font-bold">BILL OF LADING</h2>
                      <p className="text-sm text-gray-600">{selectedBOL.bol_number}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <h3 className="font-bold text-sm uppercase text-gray-600">Shipper</h3>
                        <p className="font-medium">{selectedBOL.shipper_name}</p>
                        <p className="text-sm whitespace-pre-wrap">{selectedBOL.shipper_address}</p>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-bold text-sm uppercase text-gray-600">Consignee</h3>
                        <p className="font-medium">{selectedBOL.consignee_name}</p>
                        <p className="text-sm whitespace-pre-wrap">{selectedBOL.consignee_address}</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="font-bold text-sm uppercase text-gray-600">Carrier</h3>
                      <p className="font-medium">{selectedBOL.carrier_name}</p>
                    </div>

                    <div className="border border-gray-300 rounded overflow-hidden">
                      <div className="grid grid-cols-4 bg-gray-100 text-sm font-bold">
                        <div className="p-2 border-r border-gray-300">Description</div>
                        <div className="p-2 border-r border-gray-300">Quantity</div>
                        <div className="p-2 border-r border-gray-300">Weight</div>
                        <div className="p-2">Freight</div>
                      </div>
                      <div className="grid grid-cols-4 text-sm">
                        <div className="p-2 border-r border-gray-300">{selectedBOL.commodity_description}</div>
                        <div className="p-2 border-r border-gray-300">{selectedBOL.quantity}</div>
                        <div className="p-2 border-r border-gray-300">{selectedBOL.weight} lbs</div>
                        <div className="p-2">${selectedBOL.freight_charges?.toLocaleString()}</div>
                      </div>
                    </div>

                    {selectedBOL.special_instructions && (
                      <div className="space-y-2">
                        <h3 className="font-bold text-sm uppercase text-gray-600">Special Instructions</h3>
                        <p className="text-sm">{selectedBOL.special_instructions}</p>
                      </div>
                    )}

                    {/* Signatures Section */}
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-300">
                      {['shipper', 'carrier', 'consignee'].map((type) => {
                        const status = getSignatureStatus(selectedBOL, type);
                        return (
                          <div key={type} className="text-center space-y-2">
                            <p className="text-xs uppercase text-gray-600 font-bold">{type}</p>
                            <div className="h-16 border border-gray-300 rounded flex items-center justify-center">
                              {status.signed ? (
                                <div className="text-emerald-600">
                                  <CheckCircle className="w-6 h-6 mx-auto mb-1" />
                                  <p className="text-xs">Signed</p>
                                </div>
                              ) : (
                                <p className="text-gray-400 text-sm">Not signed</p>
                              )}
                            </div>
                            {status.date && (
                              <p className="text-xs text-gray-500">
                                {new Date(status.date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sign Actions */}
                  <div className="space-y-3 pt-4">
                    <p className="text-sm text-muted-foreground">Add E-Signature</p>
                    <div className="grid grid-cols-3 gap-3">
                      {!selectedBOL.signed_by_shipper && (user?.role === 'distributor' || user?.role === 'dispatcher') && (
                        <Button
                          variant="outline"
                          onClick={() => handleSignBOL(selectedBOL.id, 'shipper')}
                          data-testid="sign-shipper-btn"
                        >
                          <PenTool className="w-4 h-4 mr-2" />
                          Sign as Shipper
                        </Button>
                      )}
                      {!selectedBOL.signed_by_carrier && (user?.role === 'driver' || user?.role === 'dispatcher') && (
                        <Button
                          variant="outline"
                          onClick={() => handleSignBOL(selectedBOL.id, 'carrier')}
                          data-testid="sign-carrier-btn"
                        >
                          <PenTool className="w-4 h-4 mr-2" />
                          Sign as Carrier
                        </Button>
                      )}
                      {!selectedBOL.signed_by_consignee && (
                        <Button
                          variant="outline"
                          onClick={() => handleSignBOL(selectedBOL.id, 'consignee')}
                          data-testid="sign-consignee-btn"
                        >
                          <PenTool className="w-4 h-4 mr-2" />
                          Sign as Consignee
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};

export default BOLManagement;
